import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { http } from '../../lib/api';
import { useAuthStore } from '../../store/auth';
import { useUiStore } from '../../store/ui';
import { useBrandingStore } from '../../store/branding';
import { connectSocket } from '../../lib/ws';
import type { TenantDto, UserDto } from '@livestreamx/shared-types';

function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
      <path d="M2 2l1.5 1.5M22 22l-1.5-1.5" />
    </svg>
  );
}

function LogoIcon() {
  return (
    <svg viewBox="0 0 40 40" width="44" height="44" aria-hidden="true">
      <rect width="40" height="40" rx="12" fill="url(#logo-grad-login)" />
      <defs>
        <linearGradient id="logo-grad-login" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
          <stop stopColor="#818cf8" />
          <stop offset="1" stopColor="#a78bfa" />
        </linearGradient>
      </defs>
      <path d="M12 26V14l6 6 6-6v12" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const CheckSvg = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>;
const MailSvg = () => <svg className="login-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>;
const LockSvg = () => <svg className="login-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>;

function ThemeToggleButton() {
  const { theme, toggleTheme } = useUiStore();
  return (
    <button type="button" className="login-theme-btn" onClick={toggleTheme}
      aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
      title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
      {theme === 'dark' ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4" /><path d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32 1.41 1.41M2 12h2m16 0h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" /></svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>
      )}
    </button>
  );
}


export function LoginPage() {
  const navigate = useNavigate();
  const auth = useAuthStore();
  const ui = useUiStore();
  const branding = useBrandingStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => { emailRef.current?.focus(); }, []);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await http.post<{ user: UserDto; tenant: TenantDto | null; accessToken: string; accessExpiresInSeconds: number }>(
        '/api/auth/login', { email, password }
      );
      if (!data.accessToken) throw new Error('No access token returned');
      auth.setSession(data.user, data.tenant, data.accessToken, data.accessExpiresInSeconds);
      void useBrandingStore.getState().load();
      connectSocket();
      ui.toast('success', 'Welcome back!');
      navigate(data.user.role === 'SUPER_ADMIN' ? '/admin' : '/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); void submit(); };

  return (
    <div className="login-page">
      {/* Left — branding */}
      <div className="login-left">
        <div className="login-left-content">
          <div className="login-left-logo">
            {branding.logoUrl ? <img src={branding.logoUrl} alt="" className="login-logo-img" /> : <LogoIcon />}
          </div>
          <h1 className="login-left-title">{branding.loginTitle?.trim() || branding.platformName || 'LiveStreamX'}</h1>
          <p className="login-left-subtitle">{branding.loginTagline?.trim() || 'Stream once, broadcast everywhere.'}</p>
          <ul className="login-left-features">
            <li><CheckSvg />Multi-destination restreaming</li>
            <li><CheckSvg />Real-time analytics dashboard</li>
            <li><CheckSvg />Free tier available — no credit card</li>
          </ul>
        </div>
        <div className="login-orb login-orb-1" aria-hidden="true" />
        <div className="login-orb login-orb-2" aria-hidden="true" />
        <div className="login-orb login-orb-3" aria-hidden="true" />
      </div>
      {/* Right — form */}
      <div className="login-right">
        <div className="login-form-card">
          <ThemeToggleButton />
          <div className="login-mobile-logo">
            {branding.logoUrl ? <img src={branding.logoUrl} alt="" className="login-logo-img" /> : <LogoIcon />}
            <span className="login-mobile-brand">{branding.platformName || 'LiveStreamX'}</span>
          </div>
          <div className="login-form-header">
            <h1 id="login-heading">Welcome back</h1>
            <p>Sign in to your account to continue</p>
          </div>
          {error && (
            <div className="login-error" role="alert">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
              <span>{error}</span>
            </div>
          )}
          <form onSubmit={handleSubmit} className="login-form" noValidate>
            <div className="login-field">
              <label htmlFor="login-email">Email address</label>
              <div className="login-input-wrap">
                <MailSvg />
                <input ref={emailRef} id="login-email" name="email" type="email" autoComplete="email" required
                  value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
              </div>
            </div>
            <div className="login-field">
              <div className="login-field-row">
                <label htmlFor="login-password">Password</label>
                <Link to="/forgot-password" className="login-forgot-link">Forgot?</Link>
              </div>
              <div className="login-input-wrap">
                <LockSvg />
                <input id="login-password" name="password" type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password" required value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" />
                <button type="button" className="login-eye-btn"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword} tabIndex={0}>
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>
            <label className="login-remember">
              <input type="checkbox" name="remember" checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)} aria-label="Remember me for 30 days" />
              <span>Remember for 30 days</span>
            </label>
            <button type="submit" disabled={busy || !email || !password} className="login-submit" aria-busy={busy}>
              {busy ? (
                <><svg className="login-spinner" viewBox="0 0 24 24" fill="none"><circle opacity="0.25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path opacity="0.75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Signing in&hellip;</>
              ) : 'Sign in'}
            </button>
          </form>
          <div className="login-footer" style={{ marginTop: '0.75rem' }}>
            New tenant?{' '}
            <Link to="/signup" style={{ color: '#818cf8', fontWeight: 600 }}>Create an account</Link>
          </div>
          {branding.supportEmail?.trim() ? (
            <div className="login-footer login-support">Need help? <a href={`mailto:${branding.supportEmail.trim()}`}>{branding.supportEmail.trim()}</a></div>
          ) : null}
          {branding.footerText?.trim() ? (
            <div className="login-footer">{branding.footerText.trim()}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
